import type { RouterOutputs } from '@package/api'

import { isGrantSeatSource, seatSourceLabel } from '../../lib/seat-source'

/**
 * One roster row.
 *
 * Every consumer of this table feeds it `myOrganization.listChildren` — the whole school on Kelola
 * murid, one class on the class page — so the row type is that procedure's element type rather than
 * a hand-written subset. `schoolClass` / `schoolClasses` are only populated when the caller asks for
 * them (`includeSchoolClass` / `includeAllSchoolClasses`), which is why the class column is opt-in.
 */
export type Student = RouterOutputs['myOrganization']['listChildren']['data'][number]

/** Filter values for the "Akses" toolbar select. */
export type AccessFilter = 'limited' | 'premium'

export interface StudentAccess {
  kind: AccessFilter
  label: string
  /** Where the seat came from. Only set for premium students. */
  sourceLabel: string | null
  /**
   * The seat's end date, shown beside its provenance. Null when there is no seat.
   *
   * Where a renewal has already been paid for, this is the RENEWED end date — see
   * `premiumScheduledEndDate`. The column answers "how long is this child covered", and the school
   * has bought the answer already.
   */
  expiresAt: Date | string | null
  /**
   * True when the date above comes from a seat that has not started yet.
   *
   * The renewal reaching the roster is the whole reason the "akan aktif" notice counts only
   * unassigned seats: a continuing student announces their own renewal, right here.
   */
  isRenewed?: boolean
  /**
   * When a seat this child already holds STARTS, for a child who holds none today.
   *
   * A renewal or an add-on can seat a student for a window that has not opened yet: they read
   * Terbatas now and turn Premium on that date. Nothing else on the row says so, and an admin who
   * has just paid for exactly that would otherwise assign them a second seat.
   */
  startsAt?: Date | string | null
}

/**
 * Reads a student's access level off the roster row.
 *
 * A student can be Premium without the organization reading the roster paying for it — a seat
 * follows the CHILD, not the organization — so the organization's own seat (`premiumId`) and the
 * outside seat (`familyPremiumId`) are two separate signals, and the organization's own wins when
 * both exist. `kind` is therefore cross-org: the badge and the "Akses: Premium" filter describe
 * access, while every seat counter beside them stays owned-scoped.
 *
 * `sourceLabel` needs `type` because both fields are relative to whoever is reading. On a school
 * roster `premiumId` is the school's seat; on a family roster the very same field is the family's.
 * And the outside seat is not always a family's — a child shared with a SECOND SCHOOL carries that
 * school's seat here — so its provenance is read from `familyPremiumOrigin` rather than assumed.
 * Getting this wrong tells a school that a family is paying for a student when another school is.
 */
export function getStudentAccess(student: Student, type: 'web' | 'school' = 'school'): StudentAccess {
  const isFamilyRoster = type === 'web'

  if (student.premiumId !== null) {
    return {
      kind: 'premium',
      label: 'Premium',
      // GRANT seats are named by flavour — only `sponsored_scholarship` is a beasiswa, and a
      // directly provisioned or pilot seat used to read as one. A seat somebody PAID for is named
      // by who pays instead: this column answers "who covers this child", and "Kursi dibeli" is a
      // pool's name on the billing page, not an answer to that. `direct_paid` is a purchase too,
      // which is why the test is the grant family and not `premiumSource === 'entitlement'`.
      sourceLabel:
        student.premiumSourceType && isGrantSeatSource(student.premiumSourceType)
          ? seatSourceLabel(student.premiumSourceType, false)
          : isFamilyRoster
            ? 'Keluarga'
            : 'Sekolah',
      // The renewed window when one has been bought, else the live seat's own. A renewal mints a
      // seat that starts the day this one ends, so the child is covered to the later date and the
      // roster would otherwise keep showing a lapse the school has already paid to avoid.
      expiresAt: student.premiumScheduledEndDate ?? student.premiumEndDate,
      isRenewed: student.premiumScheduledEndDate !== null,
    }
  }

  if (student.familyPremiumId !== null) {
    // 'Sekolah lain' only on a school roster, where a bare 'Sekolah' would be indistinguishable
    // from this school's own seat. A parent has no such ambiguity — the only school in view is
    // their child's. Unknown origin falls back to 'Keluarga': a seat with no organization at all is
    // a family subscription, which is what the query reports for that row anyway.
    return {
      kind: 'premium',
      label: 'Premium',
      sourceLabel:
        student.familyPremiumOrigin === 'school' ? (isFamilyRoster ? 'Sekolah' : 'Sekolah lain') : 'Keluarga',
      expiresAt: student.familyPremiumEndDate,
    }
  }

  return {
    kind: 'limited',
    label: 'Terbatas',
    sourceLabel: null,
    expiresAt: null,
    startsAt: student.premiumScheduledStartDate ?? null,
  }
}

/**
 * Sentinel for "students without a grade level", mirroring `NO_SCHOOL_CLASS_ID` for classes.
 *
 * The filter has to tell "not filtering" (parameter absent) apart from "students whose grade is
 * unset", and an empty string does not survive a round trip through a URL.
 */
export const NO_SCHOOL_GRADE_ID = '__no_grade__'

/**
 * Every column this table knows how to build. The source of `StudentColumnId`, and nothing else —
 * a column being buildable does not mean every roster wants it.
 *
 * Kept separate from {@link DEFAULT_STUDENT_COLUMN_IDS} deliberately: this constant used to be both,
 * so adding an id here silently added the column to every caller that renders the default set. The
 * "Orang tua" column is school-only, which is what forced the split.
 */
export const STUDENT_COLUMN_IDS = ['name', 'pin', 'schoolClass', 'schoolGrade', 'parent', 'access'] as const

export type StudentColumnId = (typeof STUDENT_COLUMN_IDS)[number]

/** What `StudentTable` renders when a caller does not pick a subset. Opt into anything beyond it. */
export const DEFAULT_STUDENT_COLUMN_IDS = [
  'name',
  'pin',
  'schoolClass',
  'schoolGrade',
  'access',
] as const satisfies readonly StudentColumnId[]

/**
 * Filter values for the parent-connection filter.
 *
 * An enum rather than a boolean because `parentConnection` has three states and the deep link
 * wants exactly one of them: `connected` means the parent holds a family account, and `pending`
 * is deliberately excluded — an unaccepted invitation is not an account anyone can subscribe with.
 */
export type ParentFilter = 'none' | 'connected'

/** Toolbar selects, independent of whether the matching column is rendered. */
export const STUDENT_FILTER_IDS = ['schoolClass', 'schoolGrade', 'access'] as const

export type StudentFilterId = (typeof STUDENT_FILTER_IDS)[number]

/**
 * Column ids the class and grade filters write to. Exported because a host page may hoist those two
 * filter values into its URL — see `StudentTableProps.columnFilters`.
 */
export const CLASS_FILTER_COLUMN: StudentColumnId = 'schoolClass'
export const GRADE_FILTER_COLUMN: StudentColumnId = 'schoolGrade'

/**
 * Column id the parent-connection filter writes to. Has no toolbar select of its own — it arrives
 * from a deep link (the premium locks' "Ajak orang tua berlangganan") and is cleared from a chip.
 */
export const PARENT_FILTER_COLUMN: StudentColumnId = 'parent'

/** One row action at a time — the table holds this and mounts exactly one dialog for it. */
export type StudentAction =
  | { kind: 'settings'; student: Student }
  | { kind: 'give-premium'; student: Student }
  | { kind: 'remove'; student: Student }
  | { kind: 'request-home-connect'; student: Student }
  | { kind: 'contact-parent'; student: Student }

/**
 * The student's link to a parent's family account, as the "Orang tua" column reads it.
 *
 * Resolved server-side in `listChildren`: `connected` comes from a family membership, never from a
 * request row's status, so a child linked by any path reads as connected here.
 */
export type ParentConnection = Student['parentConnection']

/** Narrow to the pending arm — the only one carrying a request id to resend or cancel. */
export function getPendingConnectRequest(student: Student) {
  return student.parentConnection.status === 'pending' ? student.parentConnection : null
}

/** The child's school, as the family-side delete dialog reads it. */
export type ChildSchoolConnection = Student['schoolConnection']

/**
 * Narrow to the arm that carries an outstanding removal request.
 *
 * Two conditions, not one: the child must be at a school *and* the parent must already have asked.
 * Returns null in every other case, which is what makes the dialog's primary button "Kirim
 * permintaan" rather than "Kirim ulang".
 */
export function getPendingRemovalRequest(student: Student) {
  return student.schoolConnection.status === 'connected' ? student.schoolConnection.pendingRemoval : null
}
